import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchComplianceRequest } from '../../../api/dashboard/dashboardSlice';
import { Box, Grid, Paper, Typography } from '@mui/material';
import {
  BlockOutlined as CancelledIcon,
  CancelOutlined as RejectedIcon,
  CheckCircleOutline as AcceptedIcon,
  DescriptionOutlined as DocumentIcon,
} from '@mui/icons-material';
import theme from '../../../theme';
import { lighten } from '@mui/material/styles';
import TimeRangeSelector from './TimeRangeSelector';
import SummaryBoxes from './SummaryBoxes';
import PieComplianceCard from './Pie/PieComplianceCard';
import { chain } from 'lodash';

const ComplianceSection = () => {
  const dispatch = useDispatch();
  const { complianceData, loading, error } = useSelector((state) => state.dashboard);
  const [timeRange, setTimeRange] = useState('week');

  useEffect(() => {
    const { startDate, endDate } = getDateRange(timeRange);
    dispatch(fetchComplianceRequest({ startDate, endDate }));
  }, [timeRange, dispatch]);

  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };

  const getDateRange = (range) => {
    const now = new Date();
    const endDate = now.toISOString().split('T')[0];

    let startDate = new Date(now);

    switch (range) {
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        break;
      case 'quarter':
        startDate.setMonth(now.getMonth() - 3);
        break;
      default:
        startDate.setDate(now.getDate() - 7);
    }

    startDate = startDate.toISOString().split('T')[0];

    return { startDate, endDate };
  };

  if (error)
    return (
      <Paper elevation={3} sx={{ p: 3, bgcolor: '#fff4f4' }}>
        <Typography color="error" variant="h6">
          Error: {error}
        </Typography>
      </Paper>
    );

  if (!complianceData) {
    if (loading) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography>Loading compliance data...</Typography>
        </Box>
      );
    }
    return (
      <Paper elevation={3} sx={{ p: 3, bgcolor: '#fff4f4' }}>
        <Typography color="error" variant="h6">
          No compliance data available
        </Typography>
      </Paper>
    );
  }

  const totalDocuments = complianceData?.reduce((acc, item) => acc + item.total, 0) ?? 0;
  const totalAccepted = complianceData?.reduce((acc, item) => acc + item.accepted, 0) ?? 0;
  const totalRejected = complianceData?.reduce((acc, item) => acc + item.rejected, 0) ?? 0;
  const totalCancelled = complianceData?.reduce((acc, item) => acc + item.cancelled, 0) ?? 0;

  const salesAggregated = chain(complianceData)
    .filter((item) => ['01', '02', '03', '04'].includes(item._id))
    .reduce(
      (acc, item) => {
        acc.total += item.total;
        acc.accepted += item.accepted;
        acc.rejected += item.rejected;
        acc.submitted += item.submitted;
        acc.cancelled += item.cancelled;
        return acc;
      },
      { total: 0, accepted: 0, rejected: 0, submitted: 0, cancelled: 0 },
    )
    .value();

  const purchaseAggregated = chain(complianceData)
    .filter((item) => ['11', '12', '13', '14'].includes(item._id))
    .reduce(
      (acc, item) => {
        acc.total += item.total;
        acc.accepted += item.accepted;
        acc.rejected += item.rejected;
        acc.submitted += item.submitted;
        acc.cancelled += item.cancelled;
        return acc;
      },
      { total: 0, accepted: 0, rejected: 0, submitted: 0, cancelled: 0 },
    )
    .value();

  const summaryBoxes = [
    {
      title: 'Total Active Documents',
      value: totalDocuments,
      icon: <DocumentIcon sx={{ fontSize: 40, color: `${theme.palette.info.main}` }} />,
      color: lighten(theme.palette.info.main, 0.8),
    },
    {
      title: 'Accepted Documents',
      value: totalAccepted,
      icon: <AcceptedIcon sx={{ fontSize: 40, color: `${theme.palette.success.main}` }} />,
      color: lighten(theme.palette.success.main, 0.88),
    },
    {
      title: 'Rejected Documents',
      value: totalRejected,
      icon: <RejectedIcon sx={{ fontSize: 40, color: `${theme.palette.error.main}` }} />,
      color: lighten(theme.palette.error.main, 0.88),
    },
    {
      title: 'Cancelled Documents',
      value: totalCancelled,
      icon: <CancelledIcon sx={{ fontSize: 40, color: `${theme.palette.grey[500]}` }} />,
      color: `${theme.palette.grey[100]}`,
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Compliance Overview
        </Typography>
        <TimeRangeSelector timeRange={timeRange} handleTimeRangeChange={handleTimeRangeChange} loading={loading} />
      </Box>
      <SummaryBoxes summaryBoxes={summaryBoxes} loading={loading} />
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <PieComplianceCard title="Sales" data={salesAggregated} loading={loading} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <PieComplianceCard title="Purchase" data={purchaseAggregated} loading={loading} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ComplianceSection;
